<template>
  <div class="parking-box-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('parkingBoxManage.page.title')" :subtitle="$t('parkingBoxManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.boxId" clearable class="w-full"
            :placeholder="$t('parkingBoxManage.boxIdPlaceholder')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.boxName" clearable class="w-full"
            :placeholder="$t('parkingBoxManage.boxNamePlaceholder')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.tempCarIn" clearable class="w-full"
            :placeholder="$t('parkingBoxManage.tempCarInPlaceholder')">
            <el-option value="" :label="$t('parkingBoxManage.tempCarInPlaceholder')" />
            <el-option value="Y" :label="$t('parkingBoxManage.yes')" />
            <el-option value="N" :label="$t('parkingBoxManage.no')" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('parkingBoxManage.searchBtn') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('parkingBoxManage.resetBtn') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="fetchData">
        <template #left>
          <span class="ds-panel__title">{{ $t('parkingBoxManage.listTitle') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('parkingBoxManage.page.empty')" @retry="fetchData">
        <el-table-column prop="boxId" :label="$t('parkingBoxManage.boxId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.boxId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="boxName" :label="$t('parkingBoxManage.boxName')" align="center" min-width="140" />
        <el-table-column prop="paNum" :label="$t('parkingBoxManage.parkingLot')" align="center" width="130" />
        <el-table-column prop="tempCarIn" :label="$t('parkingBoxManage.tempCarIn')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.tempCarIn === 'Y' ? 'success' : 'muted'"
              :label="scope.row.tempCarIn === 'Y' ? $t('parkingBoxManage.yes') : $t('parkingBoxManage.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="tempAuth" :label="$t('parkingBoxManage.tempAuth')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.tempAuth === 'Y' ? 'success' : 'muted'"
              :label="scope.row.tempAuth === 'Y' ? $t('parkingBoxManage.yes') : $t('parkingBoxManage.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="fee" :label="$t('parkingBoxManage.fee')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.fee === 'Y' ? 'warning' : 'muted'"
              :label="scope.row.fee === 'Y' ? $t('parkingBoxManage.yes') : $t('parkingBoxManage.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="blueCarIn" :label="$t('parkingBoxManage.blueCarIn')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.blueCarIn === 'Y' ? 'primary' : 'muted'"
              :label="scope.row.blueCarIn === 'Y' ? $t('parkingBoxManage.yes') : $t('parkingBoxManage.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="yelowCarIn" :label="$t('parkingBoxManage.yelowCarIn')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.yelowCarIn === 'Y' ? 'primary' : 'muted'"
              :label="scope.row.yelowCarIn === 'Y' ? $t('parkingBoxManage.yes') : $t('parkingBoxManage.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('parkingBoxManage.remark')" align="center" min-width="120"
          show-overflow-tooltip />
        <el-table-column :label="$t('parkingBoxManage.operation')" align="center" width="120" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="hasPrivilege('502022090904120022')" type="text" size="small"
              @click="handleOpenConsole(scope.row)">
              {{ $t('parkingBoxManage.console') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="pagination.current" :size="pagination.size" :total="pagination.total"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          {{ $t('parkingBoxManage.dataSource') }}
        </template>
      </ds-pagination-bar>
    </div>
  </div>
</template>

<script>
import { getParkingBoxList } from '@/api/car/parkingBoxManageApi'
import { jumpToIot } from '@/api/user/menuApi.js'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ParkingBoxManageList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        boxId: '',
        boxName: '',
        tempCarIn: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {

    async fetchData() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          ...this.searchForm
        }

        const res = await getParkingBoxList(params)
        this.tableData = res.data
        this.pagination.total = res.total || 0
        this.pagination.current = res.page || 1
      } catch (error) {
        console.error('获取岗亭列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('parkingBoxManage.fetchError')
        this.$message.error(this.$t('parkingBoxManage.fetchError'))
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.current = 1
      this.fetchData()
    },

    handleReset() {
      this.searchForm = {
        boxId: '',
        boxName: '',
        tempCarIn: ''
      }
      this.handleSearch()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.fetchData()
    },

    handleCurrentChange(current) {
      this.pagination.current = current
      this.fetchData()
    },

    handleSuccess() {
      this.fetchData()
    },

    handleOpenAdd() {
      this.$refs.addDialog.open()
    },

    handleOpenEdit(row) {
      this.$refs.editDialog.open(row)
    },

    handleOpenDelete(row) {
      this.$refs.deleteDialog.open(row)
    },

    handleOpenConsole(row) {
      // 跳转到控制台页面
      jumpToIot(`/#/pages/car/parkingAreaControl?paId=${row.paId}&boxId=${row.boxId}`)

    }
  }
}
</script>

<style lang="scss" scoped>
.parking-box-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
